import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useWidgetLauncherSessionState } from '@app/web/src/containers/WidgetLauncher/hooks/useWidgetLauncherSessionState';

import { TASTE_MATCH_CATALOG_INTENT_KEY } from '@lib/core/service/consts';
import { useApp } from '@lib/core/service/hooks';
import { useWidgetInstance } from '@lib/core/service/hooks/useWidgetInstance';
import { isAppInIframe } from '@lib/core/service/utils';
import { WIDGET_IFRAME_MODES } from '@lib/tools/comms/consts';
import { navigateWidgetIframe } from '@lib/tools/comms/utils';
import { RouteUtils } from '@lib/tools/routes';
import { PAGES } from '@lib/tools/views/urls';

/**
 * Closes fullscreen widget app and opens the launcher (expanded size) after taste-match → catalog navigation.
 *
 * Flow: user opens the widget app from the taste-match launcher variant, goes through quiz/auth,
 * and lands on catalog. WidgetLauncherContainer is unmounted on that route, so this hook lives in HocStart.
 *
 * WidgetLauncherTasteMatchContainer sets TASTE_MATCH_CATALOG_INTENT_KEY before navigating to the widget app.
 * This hook reads that flag and tells the host SDK to show LAUNCHER_OPENED again.
 */
export const useWidgetTasteMatchCatalogRedirect = () => {
  const location = useLocation();
  const { isFirstTimeCatalogShowed } = useApp();
  const { handleResetPageScroll } = useWidgetInstance();
  const { clearNextLauncherOpenAt, setIsLauncherOpen, isLauncherOpen, setRemainingLauncherCloseAttempts } =
    useWidgetLauncherSessionState();

  useEffect(() => {
    if (!isAppInIframe) return;

    const hasTasteMatchCatalogIntent = sessionStorage.getItem(TASTE_MATCH_CATALOG_INTENT_KEY) === 'true';
    const isCatalogPage = RouteUtils.currentPageContains(PAGES.vinhood.catalog);
    const shouldCloseWidgetAndReopenLauncher = !isFirstTimeCatalogShowed && hasTasteMatchCatalogIntent && isCatalogPage;

    if (shouldCloseWidgetAndReopenLauncher) {
      sessionStorage.removeItem(TASTE_MATCH_CATALOG_INTENT_KEY);

      // External host trigger: do not inherit a manual-close delay from a previous page.
      setRemainingLauncherCloseAttempts(0);
      clearNextLauncherOpenAt();
      handleResetPageScroll();

      if (!isLauncherOpen) {
        setIsLauncherOpen(true);
      }

      // Switch iframe from fullscreen WIDGET to open launcher (LAUNCHER_OPENED).
      navigateWidgetIframe({
        mode: WIDGET_IFRAME_MODES.LAUNCHER_OPENED,
        page: PAGES.vinhood.widgetLauncher,
      });
    }
  }, [
    location.pathname,
    isFirstTimeCatalogShowed,
    clearNextLauncherOpenAt,
    handleResetPageScroll,
    setIsLauncherOpen,
    isLauncherOpen,
    setRemainingLauncherCloseAttempts,
  ]);
};
